// AI GENERATED - REVIEW CHANGES (ADO 7833774; 242 lines updated)
import { ViewportScroller } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
  Scroll,
} from '@angular/router';
import { GaAuthService } from '@ga/central-auth-angular';
import { ProcessMonitor } from 'gain-lib/ga-process-monitor/process-monitor';
import { RouterPageViewService } from 'gain-lib/router/router-page-view.service';
import { MetricLoggerService } from 'gain-web/shared-modules/metric-logger/metric-logger.service';
import { RuntimeConfigurationService } from 'gain-web/shared-modules/runtime-configuration/runtime-configuration.service';
import { SessionTimeoutDialogComponent } from 'gain-web/shared-modules/session-timeout-dialog/session-timeout-dialog.component';
import { UserContextService } from 'gain-web/shared-modules/user/user-context.service';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import {
  isPortalLoginRedirectInFlight,
  markPortalLoginRedirectInFlight,
  stashReturnUrl,
  takeReturnUrl,
} from './return-url';
import Unit = ApiClient.Unit;

type AuthFlow =
  | 'basicLoginWithFakeTokens'
  | 'basicLoginWithHardcodedSaml'
  | 'loggedInFromPortal';

const UNAUTHORIZED_URL = '/unauthorized';
const NOT_FOUND_URL = '/not-found';
const LOGIN_REDIRECT_MARKER_KEY = 'gain.401LoginRedirectAt';
const LOGIN_REDIRECT_WINDOW_MS = 60_000;
const CHUNK_RELOAD_MARKER_KEY = 'gain.chunkLoadReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 60_000;
const CHUNK_LOAD_ERROR_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|Expected a JavaScript(-or-Wasm)? module script/i;

function wasLoginRedirectedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(LOGIN_REDIRECT_MARKER_KEY));
    const age = Date.now() - at;
    return (
      Number.isFinite(at) &&
      at > 0 &&
      age >= 0 &&
      age < LOGIN_REDIRECT_WINDOW_MS
    );
  } catch {
    return false;
  }
}

function markLoginRedirect(): void {
  try {
    sessionStorage.setItem(LOGIN_REDIRECT_MARKER_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: the redirect still proceeds, only the bound is lost.
  }
}

function clearLoginRedirectMarker(): void {
  try {
    sessionStorage.removeItem(LOGIN_REDIRECT_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

function wasChunkReloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(CHUNK_RELOAD_MARKER_KEY));
    const age = Date.now() - at;
    return (
      Number.isFinite(at) && at > 0 && age >= 0 && age < CHUNK_RELOAD_WINDOW_MS
    );
  } catch {
    return false;
  }
}

function markChunkReload(): boolean {
  try {
    const value = String(Date.now());
    sessionStorage.setItem(CHUNK_RELOAD_MARKER_KEY, value);
    return sessionStorage.getItem(CHUNK_RELOAD_MARKER_KEY) === value;
  } catch {
    // Without a stored marker the reload loop cannot be bounded.
    return false;
  }
}

function clearChunkReloadMarker(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  return (
    name === 'ChunkLoadError' ||
    (typeof message === 'string' && CHUNK_LOAD_ERROR_MESSAGE.test(message))
  );
}

/** Only same-origin absolute paths are reloaded; anything else falls back to the root. */
function toSameOriginPath(url: string): string {
  return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')
    ? url
    : '/';
}

function getHttpStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AppComponent implements OnDestroy {
  onDestroy$ = new Subject();

  @ViewChild('topOfPage') topOfPageRef!: ElementRef<HTMLDivElement>;

  public title = 'GAIN 3.0';
  public password = '';
  public authorized$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = new BehaviorSubject<boolean>(false);
  private _authFlow: AuthFlow;

  navigation = new ProcessMonitor('PENDING');

  get showBasicLogin() {
    return (
      (this._authFlow === 'basicLoginWithFakeTokens' ||
        this._authFlow === 'basicLoginWithHardcodedSaml') &&
      !this.authorized
    );
  }

  get authorized() {
    return this.authorized$.getValue();
  }

  get isLoading() {
    return this.isLoading$.getValue();
  }

  constructor(
    router: Router,
    viewportScroller: ViewportScroller,
    metricLogger: MetricLoggerService,
    private authService: GaAuthService,
    private runtimeConfigService: RuntimeConfigurationService,
    private matDialog: MatDialog,
    public userContext: UserContextService,
    private _pageView$: RouterPageViewService,
  ) {
    // Log metrics
    metricLogger.flushOnMsInterval();

    this._pageView$.pageViews$
      .pipe(takeUntilDestroyed())
      .subscribe(async ({ start, end, routeParams, queryParams, ...props }) => {
        await metricLogger.logMetric({
          name: 'PageNavigationDurationInMs',
          namespace: 'GAIN/ServiceLevelIndicators',
          value: props.durationInMs,
          unit: Unit.MILLISECONDS,
          storageResolution: null,
          properties: { ...props, ...routeParams, ...queryParams },
        });
      });

    const authOptions = this.runtimeConfigService.get().authOptions;

    if (authOptions.enabled && authOptions.localSamlData) {
      this._authFlow = 'basicLoginWithHardcodedSaml';
    } else if (authOptions.enabled) {
      this._authFlow = 'loggedInFromPortal';
    } else {
      this._authFlow = 'basicLoginWithFakeTokens';
    }

    if (
      this._authFlow === 'basicLoginWithHardcodedSaml' ||
      this._authFlow === 'loggedInFromPortal'
    ) {
      this.initAuthFlowWithCentralAuthIntegration();
    } else if (this._authFlow === 'basicLoginWithFakeTokens') {
      this.initBasicAuthFlow();
    }

    if (this._authFlow === 'loggedInFromPortal') {
      this.resolvePortalAuthOnStartup(router);
    }

    router.events
      .pipe(
        filter((e): e is Scroll => e instanceof Scroll),
        takeUntil(this.onDestroy$),
      )
      .subscribe((e: any) => {
        if (e.position) {
          // backward navigation
          viewportScroller.scrollToPosition(e.position);
        } else if (e.anchor) {
          // anchor navigation
          viewportScroller.scrollToAnchor(e.anchor);
        } else {
          // forward navigation
          this.topOfPageRef?.nativeElement?.scrollIntoView();
        }
      });

    this.recoverFromFailedNavigation(router, metricLogger);
  }

  /**
   * Keeps a failed navigation from leaving a blank router outlet (bug 7719533)
   * while reserving /unauthorized for a real 403.
   *
   * - NavigationCancel never navigates, except on a cold load where a resolver
   *   that emitted nothing, or an abort, would leave the outlet empty:
   *   /not-found.
   * - NavigationError: 403 -> /unauthorized; 401 -> portal login; anything else
   *   stays put in-app (the HTTP error interceptor already toasted) and goes to
   *   /not-found on a cold load.
   * - NavigationError from a failed lazy chunk load on a cold load: one full
   *   reload of the target URL per 60 s; a repeat within the window follows the
   *   rules above. In-app (warm) chunk failures never reload, so unsaved form
   *   state is not discarded. The load after a reload logs whether it recovered.
   *
   * "Cold load" = no NavigationEnd seen yet; `router.navigated` is not used
   * because it is also set by navigations that did not complete.
   *
   * The recovery routes sit OUTSIDE the resolver-bearing root route. Re-entry
   * is stopped by a flag and the failed URL rather than `router.url`, which a
   * failed navigation never commits.
   */
  private recoverFromFailedNavigation(
    router: Router,
    metricLogger: MetricLoggerService,
  ): void {
    let recoveryInFlight = false;
    let hasCompletedNavigation = false;
    const logChunkReloadOutcome = (
      outcome: 'recovered' | 'failed',
      url: string,
    ): void => {
      metricLogger
        .logMetric({
          name: 'ChunkLoadReloadOutcome',
          namespace: 'GAIN/ServiceLevelIndicators',
          value: 1,
          unit: Unit.COUNT,
          storageResolution: null,
          properties: { outcome, url },
        })
        .catch(() => undefined);
    };
    router.events
      .pipe(
        filter(
          (e): e is NavigationEnd | NavigationError | NavigationCancel =>
            e instanceof NavigationEnd ||
            e instanceof NavigationError ||
            e instanceof NavigationCancel,
        ),
        takeUntil(this.onDestroy$),
      )
      .subscribe((e) => {
        if (e instanceof NavigationEnd) {
          if (
            !hasCompletedNavigation &&
            wasChunkReloadedRecently() &&
            e.urlAfterRedirects !== NOT_FOUND_URL &&
            e.urlAfterRedirects !== UNAUTHORIZED_URL
          ) {
            logChunkReloadOutcome('recovered', e.urlAfterRedirects);
          }
          hasCompletedNavigation = true;
          clearLoginRedirectMarker();
          clearChunkReloadMarker();
          return;
        }
        if (isPortalLoginRedirectInFlight()) {
          // A full-page portal login redirect is already tearing the SPA down
          // (e.g. the auth guard cancelled this navigation).
          return;
        }
        if (
          e.url === UNAUTHORIZED_URL ||
          e.url === NOT_FOUND_URL ||
          recoveryInFlight
        ) {
          // The recovery navigation itself failed (or one is already running).
          return;
        }

        if (
          e instanceof NavigationError &&
          !hasCompletedNavigation &&
          isChunkLoadError(e.error)
        ) {
          if (wasChunkReloadedRecently()) {
            logChunkReloadOutcome('failed', e.url);
            clearChunkReloadMarker();
          } else if (markChunkReload()) {
            this.reloadPage(toSameOriginPath(e.url));
            return;
          }
        }

        if (e instanceof NavigationError && getHttpStatus(e.error) === 401) {
          if (
            this.canRedirectToPortalLogin() &&
            !wasLoginRedirectedRecently()
          ) {
            markLoginRedirect();
            stashReturnUrl(e.url);
            this.redirectToLogin(
              this.runtimeConfigService.get().authOptions.loginPageUrl,
            );
            return;
          }
        }

        const target = this.getRecoveryUrl(e, hasCompletedNavigation);
        if (target == null) {
          return;
        }
        recoveryInFlight = true;
        router
          .navigateByUrl(target, { replaceUrl: !hasCompletedNavigation })
          // Recovery is best-effort; a rejected recovery navigation is swallowed.
          .catch(() => undefined)
          .finally(() => {
            recoveryInFlight = false;
          });
      });
  }

  private getRecoveryUrl(
    e: NavigationError | NavigationCancel,
    hasCompletedNavigation: boolean,
  ): string | null {
    if (e instanceof NavigationCancel) {
      const leavesEmptyOutlet =
        e.code === NavigationCancellationCode.NoDataFromResolver ||
        e.code === NavigationCancellationCode.Aborted;
      return !hasCompletedNavigation && leavesEmptyOutlet
        ? NOT_FOUND_URL
        : null;
    }
    const status = getHttpStatus(e.error);
    if (status === 403 || status === 401) {
      // 401 only reaches here when a portal login redirect is not possible
      // or was already attempted within the last minute.
      return UNAUTHORIZED_URL;
    }
    return hasCompletedNavigation ? null : NOT_FOUND_URL;
  }

  private canRedirectToPortalLogin(): boolean {
    return (
      this._authFlow === 'loggedInFromPortal' &&
      !!this.runtimeConfigService.get().authOptions.loginPageUrl
    );
  }

  initBasicAuthFlow() {
    this.authorized$.next(!!localStorage.getItem('authorized'));
  }

  initAuthFlowWithCentralAuthIntegration() {
    this.authService.isAuthorized$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(this.authorized$);

    this.authService.isSessionTimedOut$
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe((isSessionTimedOut) => {
        if (isSessionTimedOut) {
          this.matDialog.open(SessionTimeoutDialogComponent, {
            height: '350px',
            width: '500px',
            disableClose: true,
          });
        }
      });
  }

  /**
   * On startup for the portal auth flow, decide what to do based on a RESOLVED
   * auth state rather than the first synchronous `isAuthorized$` emission.
   *
   * `isAuthorized$` is a synchronous map over a seeded BehaviorSubject read
   * straight from `document.cookie`, so its first value is a premature cookie
   * read, not a verified session. Acting on it (as the previous version did)
   * could fire a spurious login redirect on reload and bounce the user to the
   * home route (bug 7719533).
   *
   * Instead we call `getAccessToken()` once here — the same authoritative
   * token round-trip the API client uses. A resolved token means the session
   * is genuinely valid, so we restore the deep URL the user reloaded on. A
   * rejection means the session is genuinely gone (refresh token expired /
   * logged out), so we stash the deep URL and send the user to the portal
   * login to be returned afterwards. The redirect therefore fires only after a
   * real, resolved "not authenticated" determination.
   */
  private resolvePortalAuthOnStartup(router: Router): void {
    const authOptions = this.runtimeConfigService.get().authOptions;
    this.authService
      .getAccessToken({ requestedRoles: authOptions.requestRoles ?? [] })
      .then(() => {
        this.restoreReturnUrl(router);
      })
      .catch(() => {
        stashReturnUrl(router.url);
        this.redirectToLogin(authOptions.loginPageUrl);
      });
  }

  /** Full-page navigation to the portal login. Isolated so it can be overridden in tests. */
  protected redirectToLogin(loginPageUrl: string): void {
    markPortalLoginRedirectInFlight();
    window.location.href = loginPageUrl;
  }

  /** Full-page load of a same-origin path. Isolated so it can be overridden in tests. */
  protected reloadPage(url: string): void {
    window.location.assign(url);
  }

  /** Navigate back to a stashed URL (once) after a verified session. */
  private restoreReturnUrl(router: Router): void {
    const returnUrl = takeReturnUrl();
    if (returnUrl && returnUrl !== router.url) {
      router.navigateByUrl(returnUrl);
    }
  }

  async authenticate() {
    if (this._authFlow === 'loggedInFromPortal') {
      throw new Error(
        `This function shouldn't be called with the ${this._authFlow} auth flow`,
      );
    }

    if (this.password?.toLowerCase() === 'gesgain') {
      if (this._authFlow === 'basicLoginWithHardcodedSaml') {
        this.isLoading$.next(true);

        try {
          const saml =
            this.runtimeConfigService.get().authOptions.localSamlData;
          if (!saml) {
            throw new Error('Local SAML data not configured. Cannot login');
          }

          await this.authService.makeSamlRequest(saml);
        } catch (e) {
          alert('Saml request failed');
          console.error(e);
        } finally {
          this.isLoading$.next(false);
        }
      } else {
        this.authorized$.next(true);
        localStorage.setItem('authorized', 'true');
      }
    } else {
      alert('Password is incorrect.');
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
